import React from 'react'

import { ChevronDownIcon } from 'lucide-react'
import { observer } from 'mobx-react'

import { ISignature, PermitFieldType, SitePermitStatus } from '~/client/graph'
import ListTagCell, {
  TagCellItemKeyEnum,
} from '~/client/src/desktop/components/ListTagCell'
import { Checkbox } from '~/client/src/shared/components/shadcn-ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '~/client/src/shared/components/shadcn-ui/tooltip'
import StatusBadge from '~/client/src/shared/components/StatusBadge/StatusBadge'
import {
  companyPermitFieldTypes,
  equipmentPermitFieldTypes,
  locationPermitFieldTypes,
  materialPermitFieldTypes,
  materialResourcePermitFieldTypes,
  userPermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import Company from '~/client/src/shared/models/Company'
import LocationBase from '~/client/src/shared/models/LocationObjects/LocationBase'
import Material from '~/client/src/shared/models/Material'
import PermitField from '~/client/src/shared/models/PermitField'
import User from '~/client/src/shared/models/User'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import { NO_VALUE } from '~/client/src/shared/utils/usefulStrings'

import ReportsListTable from '../components/ReportsListTable/ReportsListTable'
import FormReportListStore, { DataKeys } from './FormReportList.store'

interface IProps {
  store: FormReportListStore
}

const renderCompanies = (companies: Company[]): JSX.Element => (
  <ListTagCell items={companies} renderItemKey={TagCellItemKeyEnum.COMPANY} />
)
const renderUsers = (users: User[]): JSX.Element => (
  <ListTagCell items={users} renderItemKey={TagCellItemKeyEnum.USER} />
)
const renderLocations = (locations: LocationBase[]): JSX.Element => (
  <ListTagCell items={locations} renderItemKey={TagCellItemKeyEnum.LOCATION} />
)
const renderEquipment = (locations: LocationBase[]): JSX.Element => (
  <ListTagCell items={locations} renderItemKey={TagCellItemKeyEnum.EQUIPMENT} />
)
const renderMaterials = (materials: Material[]): JSX.Element => (
  <ListTagCell items={materials} renderItemKey={TagCellItemKeyEnum.MATERIAL} />
)
const renderMaterialResources = (records: IResourceRecord[]): JSX.Element => (
  <ListTagCell
    items={records as any}
    renderItemKey={TagCellItemKeyEnum.MATERIAL_RESOURCE}
  />
)

@observer
export default class FormReportList extends React.Component<IProps> {
  public render() {
    const {
      rows,
      columns,
      columnsWidthState,
      groupingKey,
      collapsedCategories,
      toggleCategoryCollapsing,
      sortState,
      handleColumnSort,
      onRowClick,
      toggleCategory,
      getRowHeight,
    } = this.props.store

    return (
      <ReportsListTable
        rows={rows}
        groupingKey={groupingKey}
        sortedColumnKey={sortState.columnKey}
        sortingOrder={sortState.order}
        columns={columns}
        columnsWidthState={columnsWidthState}
        onColumnSort={handleColumnSort}
        collapsedCategories={collapsedCategories}
        columnKeyToCellRenderer={this.columnKeyToCellRenderer}
        onCategoryCollapsingToggle={toggleCategoryCollapsing}
        onCellClick={onRowClick}
        onCategoryCheckboxToggle={toggleCategory}
        rowHeightGetter={getRowHeight}
      />
    )
  }

  private get columnKeyToCellRenderer() {
    const columnsHandler = {
      [DataKeys.CHECKBOX]: value => <Checkbox checked={value} />,
      [DataKeys.NAME]: this.renderTitle,
      [DataKeys.STATUS]: this.renderStatus,
      [DataKeys.START_DATE]: (date: string) => this.renderStrings([date]),
      [DataKeys.END_DATE]: (date: string) => this.renderStrings([date]),
      [DataKeys.COMPANY]: renderCompanies,
      [DataKeys.LOCATION]: renderLocations,
      [DataKeys.RESPONSIBLE]: renderUsers,
    }

    this.props.store.dynamicColumns.forEach(dynamicColumn => {
      columnsHandler[dynamicColumn.uiKey] = this.renderDynamicColumn
    })

    return columnsHandler
  }

  private renderTitle = (
    title:
      | string
      | {
          isInspection?: boolean
          isDependentRow?: boolean
          value: string
        },
  ) => {
    if (typeof title === 'string') {
      return this.renderStrings([title])
    }
    if (title.isInspection || title.isDependentRow) {
      return <div className="pl15">{this.renderStrings([title.value])}</div>
    }
  }

  private renderDynamicColumn = (field: PermitField) => {
    if (!field) return NO_VALUE

    if (equipmentPermitFieldTypes.includes(field.type)) {
      return renderEquipment(field.values)
    }

    if (locationPermitFieldTypes.includes(field.type)) {
      return renderLocations(field.values)
    }

    if (userPermitFieldTypes.includes(field.type)) {
      return renderUsers(field.values)
    }

    if (companyPermitFieldTypes.includes(field.type)) {
      return renderCompanies(field.values)
    }

    if (materialPermitFieldTypes.includes(field.type)) {
      return renderMaterials(field.values)
    }

    if (materialResourcePermitFieldTypes.includes(field.type)) {
      return renderMaterialResources(field.values)
    }

    if (field.type === PermitFieldType.File) {
      return this.renderStrings([
        field.values?.length
          ? Localization.translator.xFiles(field.values?.length)
          : NO_VALUE,
      ])
    }

    if (field.type === PermitFieldType.Measure) {
      return this.renderStrings(
        field.values.map(fieldValue =>
          [fieldValue.value, fieldValue.units].join(' '),
        ),
      )
    }

    if (field.type === PermitFieldType.Signature) {
      return this.renderSignatures(field.values as ISignature[])
    }

    if (field.values?.length > 1) {
      return (
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex y-center" />}>
            {this.renderStrings(field.values.slice(0, 1))}
            <ChevronDownIcon className="tw:shrink-0" size={14} />
          </TooltipTrigger>
          <TooltipContent side="bottom" className="tw:flex-col tw:items-start">
            {field.values.map((value, i) => (
              <div key={value + i}>{value}</div>
            ))}
          </TooltipContent>
        </Tooltip>
      )
    }

    return this.renderStrings(field.values)
  }

  private renderStatus = (data: {
    status: SitePermitStatus
    workflowStepLevel: number
    isLate: boolean
  }) => (
    <div className="row no-flex-children">
      <StatusBadge
        status={data.status}
        workflowStepLevel={data.workflowStepLevel}
        isLate={data.isLate}
      />
    </div>
  )

  private renderStrings = (values: string[]): JSX.Element => {
    return (
      <>
        {values.map((value, i) => (
          <div key={value + i} title={value} className="text ellipsis">
            {value}
          </div>
        ))}
      </>
    )
  }

  private renderSignatures = (signatures: ISignature[]): JSX.Element => {
    const firstWithImage = signatures?.find(sig => !!sig?.dataUrl)

    if (!firstWithImage?.dataUrl) {
      return <>{NO_VALUE}</>
    }

    return (
      <img
        src={firstWithImage.dataUrl}
        alt={Localization.translator.signature}
        className="signature-report-image"
      />
    )
  }
}
